import { Form, Head } from "@inertiajs/react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsAgentProfilePath } from "@/routes"
import type { AgentProfile, BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Agent Profile", href: settingsAgentProfilePath() },
]

interface Props {
  agent_profile: AgentProfile
}

export default function Show({ agent_profile }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Agent Profile" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Agent Profile"
            description="Your title, phone, and headshot. These appear on brochures for properties you're assigned to."
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Form
                method="patch"
                action={settingsAgentProfilePath()}
                encType="multipart/form-data"
                options={{ preserveScroll: true }}
                className="space-y-4"
              >
                {({ errors, processing, recentlySuccessful }) => (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        name="title"
                        defaultValue={agent_profile.title ?? ""}
                        placeholder="e.g. Senior Leasing Agent"
                      />
                      <InputError messages={errors.title} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        defaultValue={agent_profile.phone ?? ""}
                        placeholder="e.g. (555) 123-4567"
                      />
                      <InputError messages={errors.phone} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="headshot">Headshot</Label>
                      {agent_profile.headshot_url && (
                        <img
                          src={agent_profile.headshot_url}
                          alt="Current headshot"
                          className="mb-2 h-16 w-16 rounded-full object-cover"
                        />
                      )}
                      <Input id="headshot" name="headshot" type="file" accept="image/*" />
                      <InputError messages={errors.headshot} />
                    </div>

                    <div className="flex items-center gap-4 pt-2">
                      <Button type="submit" disabled={processing}>Save Profile</Button>
                      <p className={`text-sm text-neutral-600 transition ease-in-out ${recentlySuccessful ? "opacity-100" : "opacity-0"}`}>
                        Saved
                      </p>
                    </div>
                  </>
                )}
              </Form>
            </CardContent>
          </Card>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
